import React from 'react';
import { TextureLoader, RepeatWrapping } from 'three';
import { useGLTF, PerspectiveCamera, Text } from '@react-three/drei';
import { useFormStore } from '../../state/FormStore';
import { AluminumSlidingDoor } from './AluminumSlidingDoor';
import { GlassSlidingDoor } from './GlassSlidingDoor';
import { LeftGSDoor } from './LeftGSDoor';
import { RightGSDoor } from './RightGSDoor';
import { Spie } from './Spie';
import { Zonwering } from './Zonwering';
import { SandwichPanel } from './SandwichPanel';
import { FixedPanel } from './FixedPanel';
import { AluLeftDoor } from './AluLeftDoor';
import { AluRightDoor } from './AluRightDoor';
import { parseLedPattern } from '../../utils/ledUtils';


export function Veranda(props) {
  const { nodes, materials } = useGLTF('/veranda3/veranda3.glb');
  const { nodes: AluDoornodes, materials: AluDoormaterials } = useGLTF('/veranda3/AluminiumDoor.glb');
  const { formData, getVerandaColor, getDakColor, calculateSizes, getNumberOfBeams } = useFormStore();
  const verandaColor = getVerandaColor();
  const dakColor = getDakColor();
  const verandaSize = calculateSizes();
  const numberOfBeams = getNumberOfBeams();

  const textureLoader = new TextureLoader();

  const wallTexture = textureLoader.load('textures/bricks.jpg', function (texture) {
    texture.wrapS = texture.wrapT = RepeatWrapping;
    texture.offset.set(0, 0);
    texture.repeat.set(15, 15);
  });

  const woodTexture = textureLoader.load('textures/wood.jpg', function (texture) {
    texture.wrapS = texture.wrapT = RepeatWrapping;
    texture.offset.set(0, 0);
    texture.repeat.set(10, 10);
  });

  const opaalTexture = textureLoader.load('textures/opaal.jpg', function (texture) {
    texture.wrapS = texture.wrapT = RepeatWrapping;
    texture.offset.set(0, 0);
    texture.repeat.set(10, 10);
  });
  const playTexture = textureLoader.load('textures/play.png', function (texture) {
    texture.wrapS = texture.wrapT = RepeatWrapping;
    //texture.offset.set(4, 5);
    texture.repeat.set(2, 2);
  });

  const verandaStartPosition = -formData.maat.breedte * 0.001; // -verandaSize[0] / 2;
  const verandaEndPosition = formData.maat.breedte * 0.001;

  const gap = (formData.maat.breedte * 0.002003) / (numberOfBeams - 1);
  const activeGoot = formData.gutter.goot_option;


  const voorkantAlumDoorHandle = formData.voorkant_alum_schuifdeur_options?.voorkant_schuifdeur_handle;
  const rechterAlumDoorHandle = formData.rechter_alum_schuifdeur_options?.rechter_schuifdeur_handle;
  const linkerAlumDoorHandle = formData.linker_alum_schuifdeur_options?.linker_schuifdeur_handle;

  // Check if we're in standalone doors mode
  const isStandaloneDoors = formData.purchase_mode?.mode_option === 'Standalone Doors Only';

  // Determine which door is selected for standalone mode
  const hasVoorkantDoor = formData.voorkant?.schuifdeur && formData.voorkant.schuifdeur !== 'Geen';
  const hasLinkerDoor = formData.linker?.linker_wand_option &&
    (formData.linker.linker_wand_option === 'Aluminium Schuifdeur' ||
      formData.linker.linker_wand_option === 'Glazenschuifdeur');
  const hasRechterDoor = formData.rechter?.rechter_wand_option &&
    (formData.rechter.rechter_wand_option === 'Aluminium Schuifdeur' ||
      formData.rechter.rechter_wand_option === 'Glazenschuifdeur');

  // --- LED Pattern Logic ---
  const ledEnabled = formData.verlichting?.led_option === 'Ja' && formData.verlichting?.led_patroon_option !== 'Geen';
  const ledPatternStr = formData.verlichting?.led_patroon_option || 'Geen';
  const beamLeds = ledEnabled ? parseLedPattern(ledPatternStr) : [];

  const Beams = Array.from({ length: numberOfBeams }, (_, i) => {
    const beamPosition = verandaStartPosition + i * gap;

    // Determine how many spots this specific beam gets
    // LED pattern repeats across all internal beams
    let currentBeamSpots = 0;
    const isInternalBeam = i !== 0 && i !== numberOfBeams - 1;
    if (ledEnabled && isInternalBeam && beamLeds.length > 0) {
      const internalIndex = i - 1;
      currentBeamSpots = beamLeds[internalIndex % beamLeds.length];
    }

    return (
      <group
        name="beam_and_lights"
        position={[beamPosition, 0, 0]}
        scale={[1, 1, verandaSize[2]]}
      >
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.beam_top.geometry}
          material={nodes.beam_top.material}
        />

        {/* Render LEDs based on parsed pattern */}
        {currentBeamSpots > 0 && (
          <>
            {(currentBeamSpots === 1 || currentBeamSpots >= 3) && (
              <mesh
                castShadow
                receiveShadow
                geometry={nodes.Led_centre.geometry}
                material={nodes.Led_centre.material}
              />
            )}
            {currentBeamSpots >= 2 && (
              <>
                <mesh
                  castShadow
                  receiveShadow
                  geometry={nodes.Led_left.geometry}
                  material={nodes.Led_left.material}
                />
                <mesh
                  castShadow
                  receiveShadow
                  geometry={nodes.Left_right.geometry}
                  material={nodes.Left_right.material}
                />
              </>
            )}
          </>
        )}

        <mesh
          castShadow
          receiveShadow
          geometry={nodes.beam_top.geometry}
        >
          <meshStandardMaterial attach="material" color={verandaColor[0]} />
        </mesh>
      </group>
    );
  });



  return (

    <group {...props} dispose={null}>

      {/* Only show veranda structure when NOT in standalone doors mode */}
      {!isStandaloneDoors && (
        <>
          <Text
            color="black"
            fontSize={0.03}  // Smaller text size
            maxWidth={200}
            lineHeight={1}
            letterSpacing={0.02}
            textAlign={'center'}
            font="https://fonts.gstatic.com/s/roboto/v27/KFOmCnqEu92Fr1Mu4mxP.ttf"
            anchorX="center"
            anchorY="middle"
            position={[0, -0.04, 0.3]}
            rotation={[0, 0, 0]}  // No rotation
          >
            {formData.maat.breedte} CM
          </Text>

          <Text
            color="black"
            fontSize={0.03}  // Smaller text size
            maxWidth={200}
            lineHeight={1}
            letterSpacing={0.02}
            textAlign={'center'}
            font="https://fonts.gstatic.com/s/roboto/v27/KFOmCnqEu92Fr1Mu4mxP.ttf"
            anchorX="center"
            anchorY="middle"
            position={[verandaEndPosition * 1.1, -0.03, 0]}
            rotation={[0, Math.PI / 2, 0]}  // No rotation
            scale={[verandaSize[0], 1, verandaSize[2]]}
          >
            {formData.maat.uitval} CM
          </Text>
          <Text
            color="black"
            fontSize={0.03}  // Smaller text size
            maxWidth={200}
            lineHeight={1}
            letterSpacing={0.02}
            textAlign={'center'}
            font="https://fonts.gstatic.com/s/roboto/v27/KFOmCnqEu92Fr1Mu4mxP.ttf"
            anchorX="center"
            anchorY="middle"
            position={[verandaEndPosition + 0.09, 0.2, 0.2]}
            rotation={[0, 0, Math.PI / 2]}  // No rotation
          >
            {formData.maat.doorloop_hoogte} CM
          </Text>

          <group position={[-0, 0.0, 0.0]}>
            <mesh
              castShadow
              receiveShadow
              geometry={nodes.roof_mesh.geometry}
              material={materials.roof_mat}
              scale={[verandaSize[0] - 0.004, 1, verandaSize[2]]}
            >
              <meshStandardMaterial attach="material" map={opaalTexture} color={dakColor[0]} transparent opacity={dakColor[1]} />
            </mesh>

            <mesh
              castShadow
              receiveShadow
              geometry={nodes.wall_mesh_n3d.geometry}
              material={materials.wall_mat}
              scale={[verandaSize[0], 1, verandaSize[2]]}
              position={[0, 0, 0]}
            >
              <meshStandardMaterial map={wallTexture} attach="material" />
            </mesh>

            <mesh
              castShadow
              receiveShadow
              geometry={nodes.floor_mesh_n3d.geometry}
              material={materials.floor_mat}
              scale={[verandaSize[0], 1, verandaSize[2]]}
            >
              <meshStandardMaterial map={woodTexture} attach="material" />
            </mesh>
          </group>
          <group>
            <mesh
              castShadow
              receiveShadow
              geometry={nodes.Leg_Mid.geometry}
              scale={[1, 1, verandaSize[2]]}
              position={[verandaStartPosition, 0, 0]}
              material={materials.skeleton_mat}
            >
              <meshStandardMaterial attach="material" color={verandaColor[0]} />
            </mesh>

            <mesh
              castShadow
              receiveShadow
              geometry={nodes.Leg_Mid.geometry}
              scale={[1, 1, verandaSize[2]]}
              position={[verandaEndPosition, 0, 0]}
              material={materials.skeleton_mat}
            >
              <meshStandardMaterial attach="material" color={verandaColor[0]} />
            </mesh>
          </group>
          <group position={[0, 0, 0.0]}>
            {Beams}
          </group>


          <mesh
            castShadow
            receiveShadow
            geometry={nodes.polySurface433_n3d.geometry}
            scale={[verandaSize[0], 1, verandaSize[2]]}
          >
            <meshStandardMaterial attach="material" color={verandaColor[0]} />
          </mesh>

          {activeGoot === 'Klassiek' && (


            <mesh
              castShadow
              receiveShadow
              geometry={nodes.Round_Gutter.geometry}
              material={materials['round:klassiek_complete:klassiek_cover:j1:skeleton_mat.011']}
              scale={[verandaSize[0], 1, verandaSize[2]]}>
              <meshStandardMaterial attach="material" color={verandaColor[0]} />
            </mesh>

          )}
          {activeGoot === 'Vierkant' && (
            <mesh
              castShadow
              receiveShadow
              geometry={nodes.Flat_Gutter.geometry}
              material={materials['round:klassiek_complete:klassiek_cover:j1:skeleton_mat.011']}
              scale={[verandaSize[0], 1, verandaSize[2]]}>
              <meshStandardMaterial attach="material" color={verandaColor[0]} />
            </mesh>
          )}
          {activeGoot === 'Rond' && (
            <mesh
              castShadow
              receiveShadow
              geometry={nodes.Round_Gutter.geometry}
              material={materials.skeleton_mat}
              scale={[verandaSize[0], 1, verandaSize[2]]}
            >
              <meshStandardMaterial attach="material" color={verandaColor[0]} />
            </mesh>
          )}
          <group>
            <mesh
              castShadow
              receiveShadow
              geometry={nodes.breedth_arrow.geometry}
              material={materials['Material.001']}
              scale={[verandaSize[0], 1, verandaSize[2]]}
            >
              <meshStandardMaterial attach="material" map={opaalTexture} color="#000000" transparent opacity="0.3" />
            </mesh>

            <mesh
              castShadow
              receiveShadow
              geometry={nodes.uitval_arrow.geometry}
              material={materials['Material.001']}
              scale={[verandaSize[0], 1, verandaSize[2]]}
            >
              <meshStandardMaterial attach="material" map={opaalTexture} color="#000000" transparent opacity="0.3" />
            </mesh>
            <mesh
              castShadow
              receiveShadow
              geometry={nodes.doorloop_arrow.geometry}
              material={materials['Material.001']}
              position={[verandaEndPosition - 0.6, 0, 0]}
            >
              <meshStandardMaterial attach="material" map={opaalTexture} color="#000000" transparent opacity="0.3" />
            </mesh>
          </group>

          {formData.zonwering.zonwering_option === 'Ja' && (
            <Zonwering verandaSize={verandaSize}
              verandaColor={verandaColor}
              opaalTexture={opaalTexture}
              playTexture={playTexture}
              verandaEndPosition={verandaEndPosition}
              nodes={nodes}
              materials={materials} />
          )}
        </>
      )}
      {/* Front door - show in both modes if selected */}
      {formData.voorkant.schuifdeur === 'Aluminium Schuifdeur' && (
        <AluminumSlidingDoor
          verandaSize={verandaSize}
          verandaColor={verandaColor}
          opaalTexture={opaalTexture}
          playTexture={playTexture}
          verandaEndPosition={verandaEndPosition}
          nodes={AluDoornodes}
          vnodes={nodes.play_front}
          materials={AluDoormaterials}
          handleSlide={voorkantAlumDoorHandle}
        />
      )}
      {formData.voorkant.schuifdeur === 'Glazen Schuifdeur' && (
        <GlassSlidingDoor
          verandaSize={verandaSize}
          verandaColor={verandaColor}
          opaalTexture={opaalTexture}
          playTexture={playTexture}
          verandaStartPosition={verandaStartPosition}
          nodes={nodes}
          materials={materials}
        />
      )}

      {/* Only show side panels and other components when NOT in standalone mode */}
      {!isStandaloneDoors && (
        <>
          <Spie
            verandaSize={verandaSize}
            verandaColor={verandaColor}
            opaalTexture={opaalTexture}
            verandaStartPosition={verandaStartPosition}
            nodes={nodes}
            materials={materials}
          />
          {formData.linker.linker_wand_option === 'Glazenschuifdeur' && (
            <LeftGSDoor
              verandaSize={verandaSize}
              verandaColor={verandaColor}
              opaalTexture={opaalTexture}
              playTexture={playTexture}
              verandaStartPosition={verandaStartPosition}
              nodes={nodes}
              materials={materials}
            />
          )}
          {formData.rechter.rechter_wand_option === 'Glazenschuifdeur' && (
            <RightGSDoor
              verandaSize={verandaSize}
              verandaColor={verandaColor}
              opaalTexture={opaalTexture}
              playTexture={playTexture}
              verandaStartPosition={verandaStartPosition}
              nodes={nodes}
              materials={materials}
            />
          )}
          <SandwichPanel
            verandaSize={verandaSize}
            verandaColor={verandaColor}
            opaalTexture={opaalTexture}
            verandaStartPosition={verandaStartPosition}
            nodes={nodes}
            materials={materials}
          />
          <FixedPanel
            verandaSize={verandaSize}
            verandaColor={verandaColor}
            opaalTexture={opaalTexture}
            verandaStartPosition={verandaStartPosition}
            nodes={nodes}
            materials={materials}
          />
          {formData.linker.linker_wand_option === 'Aluminium Schuifdeur' && (
            <AluLeftDoor
              verandaSize={verandaSize}
              verandaColor={verandaColor}
              opaalTexture={opaalTexture}
              playTexture={playTexture}
              verandaEndPosition={verandaEndPosition}
              nodes={AluDoornodes}
              vnodes={nodes.play_left}
              materials={AluDoormaterials}
              handleSlide={linkerAlumDoorHandle}
            />
          )}
          {formData.rechter.rechter_wand_option === 'Aluminium Schuifdeur' && (
            <AluRightDoor
              verandaSize={verandaSize}
              verandaColor={verandaColor}
              opaalTexture={opaalTexture}
              playTexture={playTexture}
              verandaEndPosition={verandaEndPosition}
              nodes={AluDoornodes}
              vnodes={nodes.play_right}
              materials={AluDoormaterials}
              handleSlide={rechterAlumDoorHandle}
            />
          )}
        </>
      )}

      {/* In standalone mode, show left/right doors if they are selected */}
      {isStandaloneDoors && (
        <>
          {formData.linker.linker_wand_option === 'Glazenschuifdeur' && (
            <LeftGSDoor
              verandaSize={verandaSize}
              verandaColor={verandaColor}
              opaalTexture={opaalTexture}
              playTexture={playTexture}
              verandaStartPosition={verandaStartPosition}
              nodes={nodes}
              materials={materials}
            />
          )}
          {formData.rechter.rechter_wand_option === 'Glazenschuifdeur' && (
            <RightGSDoor
              verandaSize={verandaSize}
              verandaColor={verandaColor}
              opaalTexture={opaalTexture}
              playTexture={playTexture}
              verandaStartPosition={verandaStartPosition}
              nodes={nodes}
              materials={materials}
            />
          )}
          {formData.linker.linker_wand_option === 'Aluminium Schuifdeur' && (
            <AluLeftDoor
              verandaSize={verandaSize}
              verandaColor={verandaColor}
              opaalTexture={opaalTexture}
              playTexture={playTexture}
              verandaEndPosition={verandaEndPosition}
              nodes={AluDoornodes}
              vnodes={nodes.play_left}
              materials={AluDoormaterials}
              handleSlide={linkerAlumDoorHandle}
            />
          )}
          {formData.rechter.rechter_wand_option === 'Aluminium Schuifdeur' && (
            <AluRightDoor
              verandaSize={verandaSize}
              verandaColor={verandaColor}
              opaalTexture={opaalTexture}
              playTexture={playTexture}
              verandaEndPosition={verandaEndPosition}
              nodes={AluDoornodes}
              vnodes={nodes.play_right}
              materials={AluDoormaterials}
              handleSlide={rechterAlumDoorHandle}
            />
          )}
        </>
      )}

    </group>
  );
}

//useGLTF.preload('/veranda3/veranda3.glb');
